<script setup lang="ts">
import MarkdownIt from 'markdown-it'
import { LEGAL_LAST_UPDATED, type LegalBlock, type LegalDocument } from '~/constants/legal/operator'

/**
 * Long legal text (Terms of Use, Privacy Policy) with a sticky table of
 * contents on large screens. The content is our own static copy, so rendering
 * its inline Markdown with v-html is safe.
 */
const props = defineProps<{
  document: LegalDocument
  /** ISO date; defaults to LEGAL_LAST_UPDATED, shared by Terms and Privacy. */
  updated?: string
}>()

const { t, locale } = useI18n()

const md = new MarkdownIt({ linkify: false })
const inline = (text: string) => md.renderInline(text)

const isParagraph = (block: LegalBlock): block is string => typeof block === 'string'

const updatedOn = computed(() => props.updated ?? LEGAL_LAST_UPDATED)
const lastUpdated = computed(() => new Date(`${updatedOn.value}T00:00:00Z`).toLocaleDateString(({ cs: 'cs-CZ', de: 'de-DE', pl: 'pl-PL' } as Record<string, string>)[locale.value] ?? 'en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
}))

const sections = computed(() => props.document.sections)
</script>

<template>
  <div class="grid gap-12 lg:grid-cols-[16rem_minmax(0,1fr)] xl:gap-20">
    <aside class="hidden lg:block">
      <nav :aria-label="t('legal.contents')" class="sticky top-28">
        <div class="mb-4 font-title text-base font-semibold text-ink dark:text-white">{{ t('legal.contents') }}</div>
        <ol class="flex flex-col gap-2 border-l border-line pl-4 dark:border-gray-800">
          <li v-for="section in sections" :key="section.id">
            <a
              :href="`#${section.id}`"
              class="text-sm leading-snug text-muted transition-colors duration-300 hover:text-ink dark:text-gray-400 dark:hover:text-white"
            >{{ section.title }}</a>
          </li>
        </ol>
      </nav>
    </aside>

    <article class="rich-text-block legal-text max-w-3xl">
      <div class="mb-8 text-base text-muted dark:text-gray-400">
        {{ t('legal.lastUpdated') }} <time :datetime="updatedOn">{{ lastUpdated }}</time>
      </div>

      <template v-for="(block, i) in document.intro" :key="`intro-${i}`">
        <p v-if="isParagraph(block)" v-html="inline(block)" />
      </template>

      <section v-for="section in sections" :id="section.id" :key="section.id" class="scroll-mt-28">
        <h2>{{ section.title }}</h2>
        <template v-for="(block, i) in section.blocks" :key="`${section.id}-${i}`">
          <p v-if="isParagraph(block)" v-html="inline(block)" />
          <ul v-else-if="'list' in block">
            <li v-for="(item, j) in block.list" :key="j" v-html="inline(item)" />
          </ul>
          <div v-else-if="'table' in block" class="mb-8 overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th v-for="heading in block.table.head" :key="heading">{{ heading }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, j) in block.table.rows" :key="j">
                  <td v-for="(cell, k) in row" :key="k" v-html="inline(cell)" />
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>
    </article>
  </div>
</template>
